import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';
import { StandardEvents } from '@shopify/events';
import { formatMoney } from '@theme/money-formatting';

/**
 * Displays the total price (unit price x quantity) for the current quantity selection,
 * resolving the correct volume-pricing tier the same way price-per-item.js does.
 * Based only on the quantity entered on the PDP — cart contents are not factored in.
 * Updates automatically when quantity or variant changes.
 *
 * @typedef {Object} PriceBreak
 * @property {number} quantity - Minimum quantity for this price tier
 * @property {number} price - Price in minor currency units (cents) for this tier
 *
 * @typedef {Object} BbdTotalPriceRefs
 * @property {HTMLElement} [bbdTotalPriceAmount] - The text element displaying the total price
 *
 * @extends {Component<BbdTotalPriceRefs>}
 */
class BbdTotalPriceComponent extends Component {
  /** @type {PriceBreak[]} */
  #priceBreaks = [];
  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();
    this.#parsePriceBreaks();
    this.#attachEventListeners();
    this.#updatePriceDisplay();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
  }

  /**
   * Parses price breaks (in minor currency units) from data attributes
   */
  #parsePriceBreaks() {
    const minQuantity = parseInt(this.dataset.minQuantity || '') || 1;
    const variantPrice = parseInt(this.dataset.variantPrice || '');

    if (!isNaN(variantPrice)) {
      this.#priceBreaks.push({ quantity: minQuantity, price: variantPrice });
    }

    const priceBreaksData = this.dataset.priceBreaks;
    if (priceBreaksData) {
      const breaks = JSON.parse(priceBreaksData);
      for (const { quantity, price } of breaks) {
        if (quantity && !isNaN(parseInt(price))) {
          this.#priceBreaks.push({ quantity: parseInt(quantity), price: parseInt(price) });
        }
      }
    }

    // Sort by quantity descending for efficient lookup
    this.#priceBreaks.sort((a, b) => b.quantity - a.quantity);
  }

  /**
   * Attaches event listeners for quantity and variant updates
   */
  #attachEventListeners() {
    const { signal } = this.#abortController;

    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#handleQuantityUpdate, { signal });

    const closestSection = this.closest('.shopify-section, dialog');
    closestSection?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, { signal });
  }

  /**
   * Handles quantity selector updates
   * @param {Event} event
   */
  #handleQuantityUpdate = (event) => {
    const form = this.closest('product-form-component');
    if (!form || !(event.target instanceof Node) || !form.contains(event.target)) return;

    this.#updatePriceDisplay();
  };

  /**
   * Handles product select (variant change) events. This component is never a morph
   * target from product-form.js, so it must refresh its own price data independently,
   * mirroring the pattern used by product-price.js.
   * @param {import('@shopify/events').ProductSelectEvent} event
   */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;

        const { html, newProduct } = detail;

        if (newProduct) {
          this.dataset.productId = newProduct.id;
        } else if (detail.productId && detail.productId !== this.dataset.productId) {
          return;
        }

        const newTotalPrice = html.querySelector(`bbd-total-price[data-block-id="${this.dataset.blockId}"]`);
        if (!newTotalPrice) return;

        this.dataset.variantPrice = newTotalPrice.dataset.variantPrice;
        this.dataset.minQuantity = newTotalPrice.dataset.minQuantity;
        this.dataset.priceBreaks = newTotalPrice.dataset.priceBreaks;
        this.dataset.moneyFormat = newTotalPrice.dataset.moneyFormat;
        this.dataset.currency = newTotalPrice.dataset.currency;

        this.#priceBreaks = [];
        this.#parsePriceBreaks();
        this.updatePriceDisplay();
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[bbd-total-price] Product select promise rejected:', error);
      });
  };

  /**
   * Gets the current quantity entered on the PDP, independent of what's already in the cart
   * @returns {number}
   */
  #getCurrentQuantity() {
    const form = this.closest('product-form-component');
    const quantityInput = /** @type {HTMLInputElement | null} */ (form?.querySelector('input[name="quantity"]'));
    if (!quantityInput) return 1;

    return parseInt(quantityInput.value) || 1;
  }

  /**
   * Updates the total price display based on current quantity
   */
  updatePriceDisplay() {
    if (!this.#priceBreaks.length || !this.refs.bbdTotalPriceAmount) return;

    const quantity = this.#getCurrentQuantity();

    // Price breaks are sorted descending, find first tier that quantity qualifies for
    const priceBreak =
      this.#priceBreaks.find((pb) => quantity >= pb.quantity) ?? this.#priceBreaks[this.#priceBreaks.length - 1];

    if (priceBreak) {
      const total = priceBreak.price * quantity;
      const { moneyFormat, currency } = this.dataset;
      this.refs.bbdTotalPriceAmount.textContent = formatMoney(total, moneyFormat || '{{amount}}', currency || '');
    }
  }

  /**
   * Private wrapper for event handlers
   */
  #updatePriceDisplay = () => {
    this.updatePriceDisplay();
  };
}

if (!customElements.get('bbd-total-price')) {
  customElements.define('bbd-total-price', BbdTotalPriceComponent);
}
